import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { 
  Layers, TrendingUp, TrendingDown, Truck, Package,
  Calendar, FileText, ArrowUpRight, Edit, Save, X
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MONTHS = [
  'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
];

const AggloSalesPage = () => {
  const { getAuthHeaders } = useAuth();
  const [sales, setSales] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingSale, setEditingSale] = useState(null);
  const [transportPaid, setTransportPaid] = useState('');
  const [loadingPaid, setLoadingPaid] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchAggloSales();
  }, [selectedMonth, selectedYear]);

  const fetchAggloSales = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API}/agglos/sales?month=${selectedMonth}&year=${selectedYear}`,
        getAuthHeaders()
      );
      setSales(response.data.sales || []);
      setSummary(response.data.summary || {});
    } catch (error) {
      console.error('Error fetching agglo sales:', error);
      toast.error('Erreur lors du chargement des ventes agglos');
    } finally {
      setLoading(false);
    }
  };

  const openEditModal = (sale) => {
    setEditingSale(sale);
    setTransportPaid(sale.transport_paid || '');
    setLoadingPaid(sale.loading_paid || '');
    setShowEditModal(true);
  };

  const handleSaveCosts = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/agglos/sales/${editingSale.id}/costs`,
        {
          transport_paid: parseFloat(transportPaid) || 0,
          loading_paid: parseFloat(loadingPaid) || 0
        },
        getAuthHeaders()
      );
      toast.success('Coûts mis à jour !');
      setShowEditModal(false);
      setEditingSale(null);
      fetchAggloSales();
    } catch (error) {
      console.error('Error updating costs:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR').format(amount || 0) + ' FCFA';
  };

  const getMarginColor = (margin) => {
    if (margin > 0) return 'text-green-600';
    if (margin < 0) return 'text-red-600';
    return 'text-slate-600';
  };

  const editMargin = (parseFloat(editingSale?.revenue) || 0) - (parseFloat(transportPaid) || 0) - (parseFloat(loadingPaid) || 0);

  if (loading) {
    return <div className="flex items-center justify-center h-full">Chargement...</div>;
  }

  return (
    <div className="space-y-6" data-testid="agglo-sales-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-4xl font-bold text-[#1B3268] tracking-tight flex items-center gap-3" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
            <Layers className="w-10 h-10" />
            Ventes Agglos
          </h1>
          <p className="text-sm text-slate-600 mt-1">Suivi des ventes agglos avec coûts transport et chargement/déchargement</p>
        </div>
      </div>

      {/* Month/Year Filter */}
      <Card className="p-4 bg-white border border-slate-200 rounded-sm">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <Label>Mois:</Label>
            <Select value={selectedMonth.toString()} onValueChange={(v) => setSelectedMonth(parseInt(v))}>
              <SelectTrigger className="w-[140px]" data-testid="agglo-month-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {MONTHS.map((m, i) => (
                  <SelectItem key={i+1} value={(i+1).toString()}>{m}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <Label>Année:</Label>
            <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
              <SelectTrigger className="w-[100px]" data-testid="agglo-year-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {[2024, 2025, 2026, 2027].map(y => (
                  <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="ml-auto text-sm text-slate-500">
            {summary.count || 0} vente(s) ce mois
          </div>
        </div>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <Card className="bg-gradient-to-br from-[#1B3268] to-[#2a4785] text-white p-5 rounded-sm" data-testid="agglo-revenue-card">
          <div className="flex items-center justify-between mb-2">
            <Layers className="w-8 h-8 opacity-80" />
            <TrendingUp className="w-5 h-5 opacity-60" />
          </div>
          <p className="text-sm opacity-80">CA Agglos</p>
          <p className="text-2xl font-bold">{formatCurrency(summary.revenue)}</p>
        </Card>

        <Card className="bg-gradient-to-br from-orange-500 to-orange-600 text-white p-5 rounded-sm" data-testid="agglo-transport-card">
          <div className="flex items-center justify-between mb-2">
            <Truck className="w-8 h-8 opacity-80" />
            <TrendingDown className="w-5 h-5 opacity-60" />
          </div>
          <p className="text-sm opacity-80">Transport Payé</p>
          <p className="text-2xl font-bold">{formatCurrency(summary.transport_paid)}</p>
        </Card>

        <Card className="bg-gradient-to-br from-slate-600 to-slate-700 text-white p-5 rounded-sm" data-testid="agglo-loading-card">
          <div className="flex items-center justify-between mb-2">
            <Package className="w-8 h-8 opacity-80" />
            <TrendingDown className="w-5 h-5 opacity-60" />
          </div>
          <p className="text-sm opacity-80">Chargement/Décharg.</p>
          <p className="text-2xl font-bold">{formatCurrency(summary.loading_paid)}</p>
        </Card>

        <Card className="bg-gradient-to-br from-red-500 to-red-600 text-white p-5 rounded-sm" data-testid="agglo-costs-card">
          <div className="flex items-center justify-between mb-2">
            <TrendingDown className="w-8 h-8 opacity-80" />
          </div>
          <p className="text-sm opacity-80">Total Dépenses</p>
          <p className="text-2xl font-bold">{formatCurrency(summary.costs_total)}</p>
        </Card>

        <Card className={`p-5 rounded-sm ${(summary.margin || 0) >= 0 ? 'bg-gradient-to-br from-green-500 to-green-600' : 'bg-gradient-to-br from-red-500 to-red-600'} text-white`} data-testid="agglo-margin-card">
          <div className="flex items-center justify-between mb-2">
            <ArrowUpRight className="w-8 h-8 opacity-80" />
          </div>
          <p className="text-sm opacity-80">Marge Nette</p>
          <p className="text-2xl font-bold">{formatCurrency(summary.margin)}</p>
        </Card>
      </div>

      {/* Total Margin Banner */}
      <Card className={`p-6 rounded-sm border-2 ${(summary.margin || 0) >= 0 ? 'border-green-500 bg-green-50' : 'border-red-500 bg-red-50'}`} data-testid="agglo-margin-banner">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-lg font-medium text-slate-700">Marge Nette Agglos (CA - Transport - Chargement)</p>
            <p className="text-sm text-slate-500">Pour {MONTHS[selectedMonth-1]} {selectedYear}</p>
          </div>
          <p className={`text-4xl font-bold ${getMarginColor(summary.margin)}`}>
            {formatCurrency(summary.margin)}
          </p>
        </div>
      </Card>

      {/* Sales Table */}
      <Card className="bg-white border border-slate-200 rounded-sm shadow-sm overflow-hidden" data-testid="agglo-sales-table">
        <div className="border-b border-slate-100 p-4 bg-slate-50/50">
          <h3 className="text-xl font-semibold text-[#1B3268]">Détail des Ventes Agglos</h3>
          <p className="text-sm text-slate-500 mt-1">Cliquez sur "Coûts" pour saisir le transport et chargement payés</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-100 text-slate-700 font-semibold uppercase text-xs tracking-wider">
                <th className="p-4 text-left">Date</th>
                <th className="p-4 text-left">Référence</th>
                <th className="p-4 text-left">Client</th>
                <th className="p-4 text-left">Chantier</th>
                <th className="p-4 text-right">CA Vente</th>
                <th className="p-4 text-right">Transport Payé</th>
                <th className="p-4 text-right">Chargement Payé</th>
                <th className="p-4 text-right">Marge</th>
                <th className="p-4 text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sales.map((sale) => (
                <tr key={sale.id} className="hover:bg-slate-50 transition-colors border-b last:border-0" data-testid={`agglo-sale-${sale.id}`}>
                  <td className="p-4">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-slate-400" />
                      <span className="font-medium">
                        {new Date(sale.date).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                      </span>
                    </div>
                  </td>
                  <td className="p-4">
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-slate-400" />
                      <span className="font-mono text-sm">{sale.reference}</span>
                      <Badge variant="outline" className={sale.type === 'facture' ? 'bg-purple-50 text-purple-700' : 'bg-blue-50 text-blue-700'}>
                        {sale.type === 'facture' ? 'Facture' : 'BL'}
                      </Badge>
                    </div>
                  </td>
                  <td className="p-4 font-medium">{sale.client_name}</td>
                  <td className="p-4 text-slate-600">{sale.chantier || '-'}</td>
                  <td className="p-4 text-right font-mono font-bold text-[#1B3268]">{formatCurrency(sale.revenue)}</td>
                  <td className="p-4 text-right font-mono">
                    {sale.costs_set ? (
                      <span className="text-orange-600">{formatCurrency(sale.transport_paid)}</span>
                    ) : (
                      <span className="text-amber-500 text-sm">À saisir</span>
                    )}
                  </td>
                  <td className="p-4 text-right font-mono">
                    {sale.costs_set ? (
                      <span className="text-slate-600">{formatCurrency(sale.loading_paid)}</span>
                    ) : (
                      <span className="text-amber-500 text-sm">À saisir</span>
                    )}
                  </td>
                  <td className="p-4 text-right">
                    <span className={`text-lg font-bold ${getMarginColor(sale.margin)}`}>
                      {sale.costs_set ? formatCurrency(sale.margin) : formatCurrency(sale.revenue)}
                    </span>
                  </td>
                  <td className="p-4 text-center">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openEditModal(sale)}
                      className="border-[#1B3268] text-[#1B3268] hover:bg-blue-50"
                      data-testid={`edit-agglo-costs-${sale.id}`}
                    >
                      <Edit className="w-4 h-4 mr-1" />
                      Coûts
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {sales.length === 0 && (
            <div className="text-center py-12 text-slate-500">
              <Layers className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>Aucune vente d'agglos pour {MONTHS[selectedMonth-1]} {selectedYear}</p>
              <p className="text-sm mt-2">Créez une vente avec le type "Agglo" dans la page Ventes</p>
            </div>
          )}
        </div>
      </Card>

      {/* Info Box */}
      <Card className="p-4 bg-blue-50 border border-blue-200 rounded-sm">
        <div className="flex gap-3">
          <Layers className="w-6 h-6 text-blue-600 flex-shrink-0" />
          <div className="text-sm text-blue-800">
            <p className="font-semibold">Comment ça marche ?</p>
            <ul className="mt-2 space-y-1 list-disc list-inside">
              <li>Les ventes d'agglos (BL et Factures) apparaissent ici automatiquement</li>
              <li>Cliquez sur <strong>"Coûts"</strong> pour saisir le transport et le chargement/déchargement payés</li>
              <li>Marge = CA Vente - Transport payé - Chargement/Déchargement payé</li>
            </ul>
          </div>
        </div>
      </Card>

      {/* Edit Costs Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="sm:max-w-[500px]" data-testid="edit-agglo-costs-modal">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-[#1B3268] flex items-center gap-2">
              <Edit className="w-6 h-6" />
              Coûts de Livraison
            </DialogTitle>
            <DialogDescription>
              {editingSale && (
                <span>
                  {editingSale.reference} - {editingSale.client_name}
                  {editingSale.chantier && ` - ${editingSale.chantier}`}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Items summary */}
            {editingSale && (
              <div className="bg-slate-50 rounded-md p-4 border border-slate-200">
                <p className="text-sm font-semibold text-slate-700 mb-2">Produits vendus</p>
                {editingSale.items.map((item, i) => (
                  <div key={i} className="flex justify-between text-sm py-1">
                    <span className="text-slate-600">{item.description} × {item.quantity} {item.unit}</span>
                    <span className="font-mono font-medium">{formatCurrency(item.total)}</span>
                  </div>
                ))}
                <div className="border-t mt-2 pt-2 flex justify-between font-bold">
                  <span>CA Total</span>
                  <span className="text-[#1B3268] font-mono">{formatCurrency(editingSale?.revenue)}</span>
                </div>
              </div>
            )}

            {/* Transport Cost */}
            <div>
              <Label className="text-sm font-semibold text-orange-700 flex items-center gap-2">
                <Truck className="w-4 h-4" />
                Transport payé (FCFA)
              </Label>
              <Input
                type="number"
                value={transportPaid}
                onChange={(e) => setTransportPaid(e.target.value)}
                placeholder="0"
                className="mt-1 h-12 text-lg border-orange-300 focus:border-orange-500"
                data-testid="transport-paid-input"
              />
            </div>

            {/* Loading Cost */}
            <div>
              <Label className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                <Package className="w-4 h-4" />
                Chargement / Déchargement payé (FCFA)
              </Label>
              <Input
                type="number"
                value={loadingPaid}
                onChange={(e) => setLoadingPaid(e.target.value)}
                placeholder="0"
                className="mt-1 h-12 text-lg border-slate-300 focus:border-slate-500"
                data-testid="loading-paid-input"
              />
            </div>

            {/* Live margin calculation */}
            <div className={`p-4 rounded-md border-2 ${editMargin >= 0 ? 'bg-green-50 border-green-300' : 'bg-red-50 border-red-300'}`}>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-600">CA Vente</span>
                  <span className="font-mono font-bold">{formatCurrency(editingSale?.revenue)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-orange-600">- Transport</span>
                  <span className="font-mono">{formatCurrency(parseFloat(transportPaid) || 0)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">- Chargement</span>
                  <span className="font-mono">{formatCurrency(parseFloat(loadingPaid) || 0)}</span>
                </div>
                <div className="border-t pt-2 flex justify-between">
                  <span className="font-bold">= Marge</span>
                  <span className={`text-xl font-bold font-mono ${getMarginColor(editMargin)}`}>
                    {formatCurrency(editMargin)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowEditModal(false); setEditingSale(null); }}>
              <X className="w-4 h-4 mr-1" /> Annuler
            </Button>
            <Button onClick={handleSaveCosts} disabled={saving} className="bg-[#1B3268] hover:bg-[#2a4785] text-white">
              <Save className="w-4 h-4 mr-1" />
              {saving ? 'Enregistrement...' : 'Enregistrer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AggloSalesPage;
